import {
  BREAKPOINT_TABLET,
  FONT_SIZE_SMALLER,
  PNL_COLORS,
  PNL_TEXT_COLORS,
  useBreakpoints,
} from "constants/constants";
import styled, { css } from "styled-components/macro";
import { camelCase } from "lodash";
import {
  darkModeTextColor,
  darkModeTitleBackgroundColor,
  lighterGrey,
} from "constants/colors";
import { useTranslation } from "react-i18next";
import { useIsDarkMode } from "store/atoms";
import { PnlBreakdownTotalTimeIndicator } from "../PnlBreakdownTotalTimeIndicator";
import { useIsTakingSnapshot } from "pages/SnapshotsPage/SnapshotElementButton";

const PNL_HEADER_ROW_HEIGHT = 26;
const BORDER_RADIUS = 4;

export function HeaderRowCell({
  title,
  rowIdx,
  dataKey,
  dataDisplay,
  flexGrow,
  isLeftmostColumnInChart,
  isRightmostColumnInChart,
  totalTime,
}: {
  title: string;
  rowIdx: number;
  dataKey: string;
  dataDisplay: any;
  flexGrow?: { sm: number; md: number };
  isLeftmostColumnInChart: boolean;
  isRightmostColumnInChart: boolean;
  totalTime?: number;
}) {
  const { t, i18n } = useTranslation("translation");
  const dataDisplayTranslated =
    // Object.values(PnlCategoryLabelsEnum).includes(dataDisplay) &&
    i18n.exists(camelCase(dataDisplay)) ? t(camelCase(dataDisplay)) : null;

  const { isTabletOrLarger } = useBreakpoints();
  const [isDarkMode] = useIsDarkMode();
  const [isTakingSnapshot] = useIsTakingSnapshot();
  return (
    <HeaderRowCellStyles
      className={`categoryTitle${
        isLeftmostColumnInChart ? " leftMost" : ""
      } title-${title} column-${dataKey}`}
      {...{
        $title: title,
        $isTabletOrLarger: isTabletOrLarger,
        $flexGrow: flexGrow,
        $isLeftmostColumnInChart: isLeftmostColumnInChart,
        $isRightmostColumnInChart: isRightmostColumnInChart,
        $isDarkMode: isDarkMode,
        $isTakingSnapshot: isTakingSnapshot,
      }}
    >
      {/* show a sum of breakdown times above the time column (dev mode only for now) */}
      {rowIdx === 0 && dataKey === "time" && totalTime ? (
        <PnlBreakdownTotalTimeIndicator {...{ totalTime }} />
      ) : null}
      {dataDisplayTranslated || dataDisplay}
    </HeaderRowCellStyles>
  );
}

export const HeaderRowCellStyles = styled.h2<{
  $title: string;
  $isTabletOrLarger: boolean;
  $flexGrow?: { sm: number; md: number };
  $isLeftmostColumnInChart: boolean;
  $isRightmostColumnInChart: boolean;
  $isDarkMode: boolean;
  $isTakingSnapshot: boolean;
}>`
  position: relative;
  .hours.zero {
    visibility: hidden;
  }
  height: ${PNL_HEADER_ROW_HEIGHT}px;
  max-height: ${PNL_HEADER_ROW_HEIGHT}px;
  margin: 20px 0 10px 0;
  padding: 6px;
  display: flex;
  align-items: center;
  font-size: ${FONT_SIZE_SMALLER}px;
  font-weight: bold;
  letter-spacing: 1px;
  white-space: nowrap;
  background: ${props => PNL_COLORS[camelCase(props.$title)]};
  color: ${props => PNL_TEXT_COLORS[camelCase(props.$title)]};
  @media (min-width: ${props =>
      props.$isTakingSnapshot ? 0 : BREAKPOINT_TABLET}px) {
    color: ${({ $isDarkMode }) => ($isDarkMode ? darkModeTextColor : "black")};
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeTitleBackgroundColor : lighterGrey};
  }
  ${props =>
    props.$flexGrow
      ? `
  flex-shrink: 0;
  flex-grow: ${props.$flexGrow.sm};
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    flex-grow: ${props.$flexGrow.md};
  }
  `
      : ""}

  ${props =>
    props.$isLeftmostColumnInChart
      ? /* first column has :after coloured decoration on tablet+ */
        css`
          border-top-left-radius: ${BORDER_RADIUS}px;
          border-bottom-left-radius: ${BORDER_RADIUS}px;
          overflow: hidden;
          @media (min-width: ${BREAKPOINT_TABLET}px) {
            padding-left: 20px;
            position: relative;
            &:before {
              content: "";
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              width: 12px;
              background: ${PNL_COLORS[camelCase(props.$title)]};
            }
            &:after {
              /* small corner radius effect on right side */
              content: "";
              position: absolute;
              top: 0;
              bottom: 0;
              left: 9px;
              width: 10px;
              border-radius: ${BORDER_RADIUS}px;
              background: ${props.$isDarkMode
                ? darkModeTitleBackgroundColor
                : lighterGrey};
            }
          }
        `
      : ""}
  ${props =>
    props.$isRightmostColumnInChart
      ? /* last column has rounded border right */
        `
  overflow: hidden;
  border-top-right-radius: ${BORDER_RADIUS}px;
  border-bottom-right-radius: ${BORDER_RADIUS}px;
  `
      : ""}
`;
